// safety/incidents-and-injuries/components/EditIncidentDrawer.tsx
// Slide-over for editing an incident's core fields (report form sections).
import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Drawer } from './shared';
import { IncidentForm } from './IncidentForm';
import type { Incident, SafetyLists, EmployeeRef, EntityRef } from '../../../../types/incident.types';

// Only the report-form fields are editable here — sub-collections, workflow
// and server-managed fields are never sent back in the update payload.
const pickEditable = (i: Incident): Partial<Incident> => ({
    employeeId: i.employeeId ?? '',
    employeeName: i.employeeName ?? '',
    entityId: i.entityId ?? '',
    entityName: i.entityName ?? '',
    project: i.project ?? '',
    supervisor: i.supervisor ?? '',
    date: i.date ? i.date.slice(0, 10) : '',
    time: i.time ?? '',
    witnesses: i.witnesses ?? '',
    cause: i.cause ?? '',
    bodyPart: i.bodyPart ?? '',
    nature: i.nature ?? '',
    description: i.description ?? '',
    firstAidApplied: i.firstAidApplied ?? null,
    firstAiderName: i.firstAiderName ?? '',
    firstAidTreatment: i.firstAidTreatment ?? '',
    stayedAtWork: i.stayedAtWork ?? null,
    hospitalised: i.hospitalised ?? null,
    fitToReturn: i.fitToReturn ?? null,
    rtwCoordinator: i.rtwCoordinator ?? '',
});

export const EditIncidentDrawer: React.FC<{
    open: boolean;
    incident: Incident;
    lists: SafetyLists;
    employees: EmployeeRef[];
    entities: EntityRef[];
    onClose: () => void;
    onSave: (values: Partial<Incident>) => Promise<void>;
}> = ({ open, incident, lists, employees, entities, onClose, onSave }) => {
    const [d, setD] = useState<Partial<Incident>>(() => pickEditable(incident));
    const [submitting, setSubmitting] = useState(false);
    const set = (patch: Partial<Incident>) => setD((prev) => ({ ...prev, ...patch }));

    // Reset the form to the incident's current values each time the drawer opens
    useEffect(() => {
        if (open) setD(pickEditable(incident));
    }, [open, incident]);

    const submit = async () => {
        setSubmitting(true);
        try {
            await onSave(d);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Drawer open={open} onClose={onClose} width="640px">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <div>
                    <h3 className="text-base font-semibold">Edit Incident</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{incident.incidentNumber}</p>
                </div>
                <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
                    <X size={20} />
                </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4">
                <IncidentForm value={d} onChange={set} lists={lists} employees={employees} entities={entities} />
            </div>
            <div className="px-5 py-3.5 border-t border-border flex justify-end gap-2">
                <button
                    onClick={onClose}
                    disabled={submitting}
                    className="h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent"
                >
                    Cancel
                </button>
                <button
                    onClick={submit}
                    disabled={submitting}
                    className="h-10 px-5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {submitting ? 'Saving…' : 'Save Changes'}
                </button>
            </div>
        </Drawer>
    );
};

export default EditIncidentDrawer;
